import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  MenuItem,
  RadioGroup,
  Radio,
  Icon,
  Menu,
  MenuDivider,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { useSelector } from "react-redux";
import GeneralSelectList from "components/GeneralSelectList";
import storage from "../../../../../utils/storage";
import "./Interpreter.scss";
import { RoomLocked } from "../../RoomLocked/RoomLocked";

const InterpreterDesktopController = ({
  languageList,
  sendInvite,
  genderOption,
  videoOption,
  favoriteSelected,
  languageSelected,
  videoOptionSelected,
  genderOptionSelected,
  isEstimatedTimeAvailable,
  cancelInvite,
}) => {
  const { t } = useTranslation();
  const { authToken, portal } = storage.getItem("user") || {};

  const estimatedTime = useSelector((state) => state.interpreter.estimatedTime);
  const interpreterRequestInProgress = useSelector(
    (state) => state.interpreter.interpreterRequestIsInProgress
  );

  const selectedGender = useSelector(
    (state) => state.interpreter.selectedGender
  );

  const isRoomLocked = useSelector(
    (state) => state.call.roomInfo?.RoomMode?.isLocked
  );

  const roomID = useSelector((state) => state.call.roomInfo?.entityID);

  const selectedLanguage = useSelector(
    (state) => state.interpreter.selectedLanguage
  );

  const currentSelectedVideoOption = useSelector(
    (state) => state.interpreter.callType
  );
  const renderLanguageItem = (item, { handleClick, modifiers }) => {
    return (
      <MenuItem
        className={item.text}
        onClick={handleClick}
        text={item.text}
        key={item.value}
        active={modifiers?.active}
        labelElement={
          item.favorite ? (
            <Icon
              className="favorite-language"
              icon={IconNames.STAR}
              onClick={() => {
                favoriteSelected(item, false);
              }}
            />
          ) : (
            <Icon
              className="not-favorite-language"
              icon={IconNames.STAR_EMPTY}
              onClick={() => {
                favoriteSelected(item, true);
              }}
            />
          )
        }
      />
    );
  };

  const renderGenderOptionItem = (item, { handleClick, modifiers }) => {
    return (
      <MenuItem
        className={item.text}
        onClick={handleClick}
        active={modifiers.active}
        text={t(item.text)}
        key={item.value}
      />
    );
  };

  const renderGroupedItemList = (listProps) => {
    const noResults = (
      <MenuItem disabled={true} text="No results." roleStructure="listoption" />
    );

    /// omit noResults if createNewItemFromQuery and createNewItemRenderer are both supplied, and query is not empty
    const createItemView = listProps.renderCreateItem();
    const maybeNoResults = createItemView != null ? null : noResults;

    const menuContent = renderGroupedMenuContent(listProps, maybeNoResults);
    if (menuContent == null && createItemView == null) {
      return null;
    }
    const createFirst = false;
    return (
      <Menu
        role="listbox"
        {...listProps.menuProps}
        ulRef={listProps.itemsParentRef}
      >
        {createFirst && createItemView}
        {menuContent}
        {!createFirst && createItemView}
      </Menu>
    );
  };

  const getGroupedItems = (items) => {
    return items.reduce((acc, item, index) => {
      if (item.favorite) {
        if (!acc[0]) {
          acc[0] = { group: "FAVORITE_LANGUAGE", index: 0, items: [], key: 0 };
        } else {
          acc[0].group = "FAVORITE_LANGUAGE";
        }
        acc[0].items.push(item);
      } else {
        if (!acc[0]) {
          acc[0] = {
            group: "SELECT_FAVORITE_LANGUAGE",
            index: 0,
            items: [],
            key: 0,
          };
        }
        if (!acc[1]) {
          acc[1] = { group: "LANGUAGES", index: 1, items: [], key: 1 };
        }
        acc[1].items.push(item);
      }

      return acc;
    }, []);
  };

  const renderGroupedMenuContent = (listProps, noResults) => {
    const groupedItems = getGroupedItems(listProps.filteredItems);
    const menuContent = groupedItems.map((groupedItem) => (
      <React.Fragment key={groupedItem.index}>
        <MenuDivider title={t(groupedItem.group)} />
        {groupedItem.items.map((item, index) =>
          listProps.renderItem(item, groupedItem.index + index)
        )}
      </React.Fragment>
    ));
    return groupedItems.length > 0 ? menuContent : noResults;
  };

  const languageItemSelect = (item) => {
    languageSelected(item);
  };
  const genderItemSelect = (item) => {
    genderOptionSelected(item);
  };

  const videoCallOptionSelected = (item) => {
    const value = parseInt(item.target.value);
    videoOptionSelected(value);
  };

  const renderInterpreterRequestInProgress = () => {
    return (
      <div className="interpreter-request-in-progress-container">
        <div className="request-detail-title">{t("REQUEST_DETAILS")}:</div>
        <div className="request-detail-data">
          {
            <div>
              {t("LANGUAGE")}: {t(selectedLanguage.text)}
            </div>
          }
          {genderOption && genderOption?.length > 0 && (
            <div>
              {t("INTERPRETER_GENDER")}: {t(selectedGender.text)}
            </div>
          )}
          {isEstimatedTimeAvailable && (
            <div>
              {t("INTERPRETER_ESTIMATED_TIME")}: {t(estimatedTime)}
            </div>
          )}
        </div>
        <button
          className="interpreter-invite-cancel-button"
          onClick={cancelInvite}
        >
          {t("CANCEL_REQUEST")}
        </button>
      </div>
    );
  };

  const renderInterpreterInviteUI = () => {
    return (
      <React.Fragment>
        {languageList && (
          <div className="language-list-container">
            <div className="language-list-title">{t("LANGUAGE")}</div>
            <GeneralSelectList
              items={languageList}
              className="language-list"
              customRenderItem={renderLanguageItem}
              onItemSelect={languageItemSelect}
              selectedItemName={t(selectedLanguage?.text) || ""}
              portalClassName="language-selection-list"
              itemListRenderer={renderGroupedItemList}
              matchTargetWidth={true}
              filterable={true}
              itemPredicate={(query, language, _index, exactMatch) => {
                const normalizedTitle = language.text.toLowerCase();
                const normalizedQuery = query.toLowerCase();

                if (exactMatch) {
                  return normalizedTitle === normalizedQuery;
                } else {
                  return `${normalizedTitle}`.indexOf(normalizedQuery) >= 0;
                }
              }}
            />
          </div>
        )}
        {genderOption && genderOption?.length > 0 && (
          <div className="gender-list-container">
            <div className="gender-list-title">{t("INTERPRETER_GENDER")}</div>
            <GeneralSelectList
              items={genderOption}
              className="gender-list"
              customRenderItem={renderGenderOptionItem}
              onItemSelect={genderItemSelect}
              selectedItemName={t(selectedGender?.text) || ""}
              matchTargetWidth={true}
              portalClassName="gender-selection-list"
            />
          </div>
        )}
        {videoOption && videoOption?.length > 0 && (
          <RadioGroup
            label={t("CALL_TYPE_LABEL")}
            onChange={videoCallOptionSelected}
            className="video-option-container"
            selectedValue={currentSelectedVideoOption}
          >
            {videoOption.map((option) => {
              return (
                <Radio
                  label={t(option.text)}
                  value={option.value}
                  key={option.value}
                />
              );
            })}
          </RadioGroup>
        )}
        <div className="invite-button-container">
          <button
            className={`interpreter-invite-button ${
              !isEstimatedTimeAvailable
                ? "interpreter-invite-button-offset"
                : ""
            }`}
            disabled={
              selectedGender.text === "SELECT_GENDER_PLACEHOLDER" ||
              selectedLanguage.text === "SELECT_LANGUAGE_PLACEHOLDER"
            }
            onClick={sendInvite}
          >
            {t("INVITE")}
          </button>
        </div>
      </React.Fragment>
    );
  };

  const renderLockRoomUI = () => {
    return <RoomLocked portal={portal} authToken={authToken} roomID={roomID} />;
  };

  const renderInterpreterTabContent = () => {
    if (isRoomLocked === "true" || isRoomLocked === true) {
      return renderLockRoomUI();
    }
    if (interpreterRequestInProgress) {
      return renderInterpreterRequestInProgress();
    } else {
      return renderInterpreterInviteUI();
    }
  };

  return (
    <div className="invite-interpreter-popup-container">
      <div className="interpreter-popup-title">
        {interpreterRequestInProgress
          ? t("INVITE_INTERPRETER_IN_PROGRESS_POPUP_TITLE")
          : t("INVITE_INTERPRETER_POPUP_TITLE")}
      </div>
      {renderInterpreterTabContent()}
    </div>
  );
};

export default memo(InterpreterDesktopController);
